import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ResultItem } from "@/services/private-office-service/types";
import { EmptyData } from "./empty-data/empty-data";
import { PRIVATE_OFFICE_SUB_TYPES } from "@/features/private-room/list/adapters/get-privateroom/get-privateroom.types";
import {
  SquareArrowOutUpRightIcon,
  InfoIcon,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CameraIcon,
  CameraOffIcon,
} from "lucide-react";
import { printPrice } from "@/utils/print-price";
import { UpdatedPrivateOfficeBadge } from "@/features/private-room/list/utils/updated-private-office-badge";
import { StatusBedge } from "./status-bedge";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useNavigate, useLocation } from "react-router-dom";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { useAuth } from "@/features/authentication";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@radix-ui/react-tooltip";
import { useState, useMemo } from "react";
import { convertPOCategoryToLabel } from "@/utils/convert-po-category-to-label";
import { parseDaysSinceUpdate } from "@/utils/parse-days-since-update";
import { getPictures } from "@/features/private-room/utils/getPictures";

type SortDirection = "asc" | "desc" | null;
type SortableColumn =
  | "search_id"
  | "space_name"
  | "cityname"
  | "neighborhood"
  | "capacity"
  | "monthly_price"
  | "updated_at"
  | "status"
  | "category";

interface DataTableContainerProps {
  parseData: ResultItem[];
  isLoadingPrivateOffices: boolean;
  hasSearched?: boolean;
}

export const DataTableContainer = ({
  parseData,
  isLoadingPrivateOffices,
  hasSearched = false,
}: DataTableContainerProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { userInfo } = useAuth();

  const [sortColumn, setSortColumn] = useState<SortableColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const getSortValue = (
    item: ResultItem,
    column: SortableColumn,
  ): string | number => {
    switch (column) {
      case "search_id":
        return item.label;
      case "space_name":
        return item.space_details.name;
      case "cityname":
        return item.space_details.cityname;
      case "neighborhood":
        return item.space_details.neighborhood;
      case "capacity":
        return item.capacity;
      case "monthly_price":
        return item.monthly_price;
      case "updated_at":
        return new Date(item.details.updated_at).getTime();
      case "status":
        return item.status;
      case "category":
        return item.details.category || 0;
      default:
        return "";
    }
  };

  const handleSort = (column: SortableColumn) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortDirection(null);
        setSortColumn(null);
      } else {
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getEmptyDataMessage = () => {
    if (hasSearched) {
      return {
        title: "Nenhum resultado encontrado",
        subtitle1: "Tente ajustar os filtros ou fazer uma nova busca.",
      };
    }

    return {
      title: "Inicie sua busca!",
      subtitle1:
        "Digite um endereço, espaço ou referência e clique em 'Buscar' para iniciar.",
    };
  };

  const emptyDataMessage = getEmptyDataMessage();

  const sortedData = useMemo(() => {
    if (!sortColumn || !sortDirection) {
      return parseData;
    }

    return [...parseData].sort((a, b) => {
      const aValue = getSortValue(a, sortColumn);
      const bValue = getSortValue(b, sortColumn);

      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.localeCompare(bValue, "pt-BR", {
          numeric: true,
          sensitivity: "base",
        });
        return sortDirection === "asc" ? comparison : -comparison;
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [parseData, sortColumn, sortDirection]);

  const tableHeaders = [
    { name: "Sala", columnName: "search_id" as SortableColumn },
    { name: "", columnName: "pictures" as const },
    { name: "Espaço", columnName: "space_name" as SortableColumn },
    { name: "Cidade", columnName: "cityname" as SortableColumn },
    { name: "Bairro", columnName: "neighborhood" as SortableColumn },
    { name: "Capacidade", columnName: "capacity" as SortableColumn },
    { name: "Mensal", columnName: "monthly_price" as SortableColumn },
    { name: "Atualizado há", columnName: "updated_at" as SortableColumn },
    { name: "Status", columnName: "status" as SortableColumn },
    { name: "Categoria", columnName: "category" as SortableColumn },
    { name: "Detalhes", columnName: "details" as const },
  ];

  const getSortIcon = (columnName: string) => {
    if (columnName === "details" || columnName === "pictures") return null;

    const column = columnName as SortableColumn;
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-4 h-4" />;
    }

    if (sortDirection === "asc") {
      return <ArrowUp className="w-4 h-4" />;
    } else if (sortDirection === "desc") {
      return <ArrowDown className="w-4 h-4" />;
    }

    return <ArrowUpDown className="w-4 h-4" />;
  };

  return (
    <div className="mt-3 bg-white rounded-lg border border-gray-200">
      <div className="max-h-[68vh] overflow-auto min-w-[97vw]  ">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-white">
            <TableRow className="bg-white">
              {tableHeaders.map((header, index) => (
                <TableHead
                  key={header.columnName}
                  className={`bg-white sticky top-0 z-20 ${index === 0 ? "px-10" : "py-4"} ${
                    header.columnName !== "details" &&
                    header.columnName !== "pictures"
                      ? "cursor-pointer hover:bg-gray-50 select-none"
                      : ""
                  }`}
                  onClick={() => {
                    if (
                      header.columnName !== "details" &&
                      header.columnName !== "pictures"
                    ) {
                      handleSort(header.columnName as SortableColumn);
                    }
                  }}
                >
                  <div className="flex items-center gap-2 text--950">
                    {header.name}
                    {getSortIcon(header.columnName)}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoadingPrivateOffices ? (
              <>
                {Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={tableHeaders.length} className="px-10">
                      <Skeleton className="w-full h-[40px] rounded-full" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : sortedData?.length > 0 ? (
              sortedData.map((item) => (
                <TableRow key={item.private_room_id}>
                  <TableCell className="font-medium py-5 px-10">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="text-ellipsis whitespace-nowrap overflow-hidden max-w-[100px]">
                            {item.label}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          className="bg-black text-white rounded px-3 py-2 text-base shadow-lg"
                        >
                          <div>{item.label}</div>
                          <div className="text-xs text-gray-300">
                            ID: {item.search_id}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <div className="text-xs text-gray-500 text-ellipsis whitespace-nowrap overflow-hidden max-w-[100px]">
                      {item.search_id}
                    </div>
                    {item.sub_type === PRIVATE_OFFICE_SUB_TYPES.LEGACY && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="inline-block">
                              <Badge
                                variant="secondary"
                                className="w-fit text-xs text-orange-600 bg-orange-50 border-0 mt-1 font-normal"
                              >
                                Sala Legada
                              </Badge>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            className="bg-black text-white rounded px-3 py-2 text-sm shadow-lg max-w-[200px] ml-2"
                          >
                            <p>
                              Esta sala é um registro legado. Ela é exibida
                              apenas para fins históricos e de consulta.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {item.origin === "integration" && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="inline-block">
                              <Badge
                                variant="outline"
                                className="w-fit text-xs text-blue-600 border-blue-300 bg-blue-50 mt-1 font-normal cursor-default"
                              >
                                Integração
                              </Badge>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            className="bg-black text-white rounded px-3 py-2 text-sm shadow-lg max-w-[260px] ml-2"
                          >
                            Esta sala é originada de uma integração com um
                            sistema parceiro. Ela não pode ser administrada.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </TableCell>
                  <TableCell className="py-5 px-4">
                    {getPictures(item.details).length > 0 ? (
                      <CameraIcon
                        id={`photos-modal-button-${item.private_room_id}`}
                        className="cursor-pointer w-5 h-5 text-gray-700 hover:text-primary transition-colors"
                        onClick={() => {
                          const targetPath = `/private-office/admin/${item.private_room_id}/photos`;
                          navigate(targetPath);
                          sendTrackDataServer({
                            url: pathname,
                            eventName: "VIEW_PHOTO_CLICKED",
                            productType: "PO",
                            data: {
                              room_id: item.private_room_id,
                            },
                          });
                        }}
                      />
                    ) : (
                      <CameraOffIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </TableCell>
                  <TableCell className="font-medium py-5">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="text-ellipsis whitespace-nowrap overflow-hidden max-w-[120px]">
                            {item.space_details.name}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          className="bg-black text-white rounded px-3 py-2 text-base shadow-lg"
                        >
                          {item.space_details.name}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium py-5">
                      {item.space_details.cityname}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium py-5">
                      {item.space_details.neighborhood}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      {item.capacity} {item.capacity > 1 ? "pessoas" : "pessoa"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>{printPrice(item.monthly_price)}</div>
                  </TableCell>
                  <TableCell className="max-w-[120px] min-w-[140px]">
                    <div className="flex items-center gap-2">
                      <UpdatedPrivateOfficeBadge
                        days={
                          parseDaysSinceUpdate(item.days_since_update) ??
                          undefined
                        }
                        date={new Date(item?.details?.updated_at || "")}
                        dateAcknowledge={undefined}
                      />
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => {
                                navigate(
                                  `/private-office/admin/${item.private_room_id}/update-history`,
                                );
                                sendTrackDataServer({
                                  url: pathname,
                                  eventName: "VIEW_UPDATE_HISTORY_CLICKED",
                                  productType: "PO",
                                  data: {
                                    private_office_id: String(
                                      item.private_room_id,
                                    ),
                                    executed_by_user_id: userInfo.user?.id ?? 0,
                                    room_id: String(item.private_room_id),
                                    room_name: item.label,
                                    space_id: item.space_id,
                                    space_name: item.space_details.name,
                                  },
                                });
                              }}
                              className="p-1"
                            >
                              <InfoIcon className="w-4 h-4 text-primary cursor-pointer" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            className="bg-black text-white rounded px-3 py-2 text-base shadow-lg"
                          >
                            Clique para ver o que foi atualizado nessa sala
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBedge
                      status={item.status}
                      type="STATUS_PRIVATE_OFFICE"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium py-5">
                      {convertPOCategoryToLabel(item?.details?.category || 0)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        className={cn(
                          "flex shadow-inherit border border-gray-300 max-h-8 bg-transparent items-center content-center rounded-sm px-2 py-0 gap-2 text-black hover:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                        )}
                        onClick={() => {
                          navigate(
                            `/private-office/admin/${item.private_room_id}/details`,
                          );
                          sendTrackDataServer({
                            url: pathname,
                            eventName: "VIEW_PO_DETAIL_CLICKED",
                            productType: "PO",
                            data: {
                              room_id: item.private_room_id,
                            },
                          });
                        }}
                      >
                        <SquareArrowOutUpRightIcon
                          className="text-zinc-950 hover:text-zinc-950"
                          width={16}
                        />
                        Ver detalhes
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableHeaders.length}>
                  <EmptyData
                    title={emptyDataMessage.title}
                    subtitle1={emptyDataMessage.subtitle1}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
